import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { useAuth } from '../../auth-provider/auth-context'
import { axios } from '../axios'
import { showNotificationWithError, showNotificationWithSuccess } from '../helpers'
import { UserRoleSchema, UserWithBranchesSchema } from './types'

export async function getMeQuery(token: string) {
  const response = await axios.get('/v2/provider/user/me', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return UserWithBranchesSchema.parse(response.data)
}

// API hooks
export function useGetMe(options: { nullWhenUnauthenticated?: boolean } = {}) {
  const { isAuthenticated, token } = useAuth()
  return useQuery({
    queryKey: ['users', 'details', 'me', options],
    queryFn: async () => {
      if (options.nullWhenUnauthenticated && !isAuthenticated) {
        return null
      }

      if (!token) {
        throw new Error('No token found')
      }

      return await getMeQuery(token)
    },
  })
}

export function useUserDetail(userId: string) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['users', 'details', userId],
    queryFn: async () => {
      if (!token) {
        throw new Error('No token found')
      }

      const response = await axios.get(`/v2/provider/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return UserWithBranchesSchema.parse(response.data)
    },
  })
}

export const ListUsersResponseSchema = z.object({
  items: z.array(UserWithBranchesSchema),
  count: z.number(),
})

export type ListUsersResponse = z.infer<typeof ListUsersResponseSchema>

export function useListUsers() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['users', 'list'],
    queryFn: async () => {
      if (!token) {
        throw new Error('No token found')
      }
      const response = await axios.get('/v2/provider/user', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return ListUsersResponseSchema.parse(response.data)
    },
  })
}

export const UpdateUserBodySchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().nullable(),
  branchIds: z.array(z.string()),
  role: UserRoleSchema,
  emailNotification: z.boolean(),
  textNotificationsEnabled: z.boolean(),
})

export type UpdateUserBody = z.infer<typeof UpdateUserBodySchema>

export function useUpdateUserMutation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateUserBody) => {
      if (!token) {
        throw new Error('No token found')
      }
      await axios.put(
        `/v2/provider/user/${data.id}`,
        {
          ...data,
          phone: data.phone?.replace(/\D/g, '') || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
    },
    onSuccess: (_, { id }) => {
      showNotificationWithSuccess('User updated successfully', 'Success')
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'details', id] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to update user')
    },
  })
}

export function useDeleteUserMutation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      if (!token) {
        throw new Error('No token found')
      }
      const response = await axios.delete(`/v2/provider/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return { data: response.data, userId }
    },
    onSuccess: ({ userId }) => {
      showNotificationWithSuccess('User deleted successfully', 'Success')
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'details', userId] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to delete user')
    },
  })
}

export const createUserBodySchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  branchIds: z.array(z.string()),
  role: UserRoleSchema,
  emailNotification: z.boolean(),
  textNotificationsEnabled: z.boolean(),
})

export type CreateUserBody = z.infer<typeof createUserBodySchema>

export function useCreateUserMutation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateUserBody) => {
      if (!token) {
        throw new Error('No token found')
      }
      await axios.post(
        '/v2/provider/user',
        {
          ...data,
          phone: data.phone?.replace(/\D/g, '') || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      return
    },
    onSuccess: () => {
      showNotificationWithSuccess('Success', 'User created successfully')
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to create user')
    },
  })
}

export type ChangePasswordData = {
  old_password: string
  new_password: string
}

export type UpdateUserSettingsData = {
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  branchIds: string[]
  role: z.infer<typeof UserRoleSchema>
  emailNotification: boolean
  textNotificationsEnabled: boolean
}

export function useChangePasswordMutation() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: ChangePasswordData }) => {
      if (!token) {
        throw new Error('No token found')
      }
      const response = await axios.post(`/v2/provider/user/${userId}/password/update`, data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    },
    onSuccess: () => {
      showNotificationWithSuccess('Password updated successfully', 'Success')
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to update password')
    },
  })
}

export function useUpdateUserSettingsMutation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, formData }: { userId: string; formData: UpdateUserSettingsData }) => {
      if (!token) {
        throw new Error('No token found')
      }
      const response = await axios.put(`/v2/provider/user/${userId}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    },
    onSuccess: (data) => {
      showNotificationWithSuccess('User settings updated successfully', 'Success')
      queryClient.invalidateQueries({ queryKey: ['users', 'details', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'detail', data.id] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to update user settings')
    },
  })
}
